/**
 * Employee Web — `/me/payroll` route (EW-21, Plan 05.2-13).
 *
 * Mounts <PayrollPage> — LIVE:
 *   - Payslip history table is LIVE (wires `GET /me/payslips` via
 *     `useMyPayslips`; runtime-validated via PayslipLineView Zod boundary
 *     for Risk #3 status-string drift defence).
 *   - Persistent privacy banner (CONTEXT D-03 — non-dismissible).
 *   - Tax-cert + salary-letter modals run the live document-request
 *     lifecycle (create → poll → presign-download) via useDocumentRequest*.
 *   - Bank-change CTA routes to `/me/requests/new?type=document` (existing
 *     flow — write surface deferred to "Self-service compensation" phase
 *     post-Phase 10).
 *
 * Breadcrumb label resolves via the `payroll` namespace (allowlisted in
 * BREADCRUMB_NAMESPACES since Plan 05.2-01).
 */
import type { JSX } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { PayrollPage } from "@linkhr/employee-self-service";
import { FeatureFence } from "../features/jurisdiction/FeatureFence";

export const Route = createFileRoute("/_auth/me/payroll")({
  staticData: { breadcrumb: { labelKey: "payroll:page.title" } },
  // `?payslip=<lineId>` — the assistant's PAYSLIP ENTITY_LINK destination
  // (CC-428/be-779). PayrollPage consumes it once and strips it; without this
  // entry the router drops the unknown param before the page ever sees it.
  validateSearch: z.object({ payslip: z.string().optional() }),
  component: MePayrollRoute,
});

function MePayrollRoute(): JSX.Element {
  return (
    <FeatureFence feature="payroll" comingSoonKey="payroll_jurisdiction">
      <PayrollPage />
    </FeatureFence>
  );
}
